// React
import { FunctionComponent, useMemo, useState } from "react"

// UI Library
import { Button, Form, Modal, Spinner } from "react-bootstrap"
import Select from "react-select"

// Local Components
import VehicleIncidentsMiniInfo from "../VehicleIncidentsMiniInfo/VehicleIncidentsMiniInfo"

// Interfaces
import type { I_IncidentAction } from "../../interfaces"

// Services
import VIS from "../../VehicleIncidentsService"
import approvalService from "src/components/approvals/ApprovalService"
import snackBar from "src/utilities/snackBar/SnackBar"

// CRUD
import patchSafetyEventEmployee from "../../crud/PatchSafetyEventEmployee"

// Styles
import "./VehicleIncidentsEditEmployee.css"

interface Props {
    incidentAction: I_IncidentAction
}

interface Option {
    label: string
    value: number
}

const formatEmployeeName = (emp: { FirstName: string; MiddleInit: string | null; LastName: string }) =>
    [emp.FirstName, emp.MiddleInit, emp.LastName].filter(Boolean).join(" ")

const VehicleIncidentsEditEmployee: FunctionComponent<Props> = ({ incidentAction }) => {
    const { retrieveEventFromMap, setIncidentAction, updateEventEmployee } = VIS

    const event = retrieveEventFromMap(incidentAction.id || -1)

    // Every active employee company-wide, already cached by approvalService on page init.
    const employeeOptions = useMemo<Option[]>(
        () =>
            Array.from(approvalService.employeeMap.values())
                .map((emp) => ({ value: emp.EmployeeID, label: formatEmployeeName(emp) }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        []
    )

    const currentEmployeeId = event?.EmployeeID ?? -1

    const [selected, setSelected] = useState<Option | null>(
        () => employeeOptions.find((o) => o.value === currentEmployeeId) ?? null
    )
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string | null>(null)

    if (!event) return null

    const isUnchanged = !selected || selected.value === event.EmployeeID

    const handleSave = async () => {
        if (isUnchanged) return

        setBusy(true)
        setError(null)

        const success = await patchSafetyEventEmployee(event.ObjectID, selected.value)

        if (success) {
            updateEventEmployee(event.ObjectID, selected.value)
            setIncidentAction({ action: -1, id: -1 })
            snackBar({ type: "primary", text: "Employee updated.", timeout: 4000 })
        } else {
            setError("Failed to update employee. Please try again.")
        }

        setBusy(false)
    }

    return (
        <div className="VehicleIncidentsEditEmployee">
            <Modal
                id="vehicle-incidents-edit-employee-modal"
                show
                backdrop="static"
                keyboard
                centered
                className="VehicleIncidentsWorkflowModal"
                animation={false}
            >
                <Modal.Header>
                    <div className="VehicleIncidentsEditEmployeeHeader VehicleIncidentsWorkflowModalHeader">
                        <p>Edit Employee</p>
                        {busy && (
                            <Spinner
                                className="VehicleIncidentsWorkflowModalHeaderSpinner"
                                animation="border"
                            />
                        )}
                    </div>
                </Modal.Header>

                <Modal.Body>
                    <div className="VehicleIncidentsEditEmployeeBody">
                        <VehicleIncidentsMiniInfo id={incidentAction.id} />

                        <Form.Group className="VehicleIncidentsEditEmployeePickerGroup">
                            <Form.Label htmlFor="vehicle-incidents-edit-employee-select">
                                Employee
                            </Form.Label>
                            <Select
                                inputId="vehicle-incidents-edit-employee-select"
                                placeholder="Search employees..."
                                value={selected}
                                onChange={(opt: Option | null) => setSelected(opt)}
                                options={employeeOptions}
                                isDisabled={busy}
                                isClearable={false}
                                // react-select's menu is clipped by the Bootstrap modal without this
                                menuPortalTarget={document.body}
                                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                            />
                        </Form.Group>

                        {error && (
                            <div className="VehicleIncidentsEditEmployeeError">{error}</div>
                        )}
                    </div>
                </Modal.Body>

                <Modal.Footer>
                    <div className="VehicleIncidentsEditEmployeeFooter VehicleIncidentsGenericModalFooter">
                        <div>
                            <Button
                                variant="secondary"
                                onClick={() => setIncidentAction({ action: -1, id: -1 })}
                                disabled={busy}
                            >
                                Cancel
                            </Button>
                        </div>
                        <div>
                            <Button
                                variant="primary"
                                onClick={handleSave}
                                disabled={busy || isUnchanged}
                                title={isUnchanged ? "Select a different employee to save" : undefined}
                            >
                                {busy ? "Saving..." : "Save"}
                            </Button>
                        </div>
                    </div>
                </Modal.Footer>
            </Modal>
        </div>
    )
}

export default VehicleIncidentsEditEmployee
